import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ClientFormDialog from "@/components/dialogs/ClientFormDialog";
import ClientDetailSheet from "@/components/sheets/ClientDetailSheet";
import StatusBadge from "@/components/StatusBadge";
import api from "@/lib/api";
import { Plus, Search } from "lucide-react";

export default function ClientAccounts() {
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(searchParams.get("open"));

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const filtered = clients.filter((c) =>
    [c.company_name, c.poc_name, c.engagement_scope].some((v) => (v || "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <DashboardLayout title="Client Accounts">
      <div className="flex items-center justify-between mb-5">
        <div className="relative w-72">
          <Search className="h-4 w-4 text-[#4B5563] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="client-search-input"
            placeholder="Search company, POC, scope..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button data-testid="add-client-button" onClick={() => setAddOpen(true)} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Add Client
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="client-accounts-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
              <th className="px-5 py-3 font-medium">Company</th>
              <th className="px-5 py-3 font-medium">POC</th>
              <th className="px-5 py-3 font-medium">Engagement Scope</th>
              <th className="px-5 py-3 font-medium">Contract Start</th>
              <th className="px-5 py-3 font-medium">Project End</th>
              <th className="px-5 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-[#4B5563]">No clients found.</td></tr>
            )}
            {filtered.map((c) => (
              <tr
                key={c.id}
                data-testid={`client-row-${c.id}`}
                onClick={() => setSelectedId(c.id)}
                className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] cursor-pointer transition-colors duration-200"
              >
                <td className="px-5 py-3 font-medium text-[#1C1C1E]">{c.company_name}</td>
                <td className="px-5 py-3 text-[#4B5563]">{c.poc_name}</td>
                <td className="px-5 py-3 text-[#4B5563] max-w-[260px] truncate">{c.engagement_scope}</td>
                <td className="px-5 py-3 text-[#4B5563]">{c.contract_start_date}</td>
                <td className="px-5 py-3 text-[#4B5563]">{c.project_end_date || "—"}</td>
                <td className="px-5 py-3"><StatusBadge value={c.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ClientFormDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <ClientDetailSheet clientId={selectedId} open={!!selectedId} onOpenChange={(v) => !v && setSelectedId(null)} onUpdated={load} />
    </DashboardLayout>
  );
}
